import { LIST_REQUESTS_AS_STAFF } from './request.queries';
import { IApolloCacheParams } from '../../../lib/cache.types';
import { IListRequestAsStaffResults, IListRequestAsStaffVariables, Pagination } from './request.types';
import { DEFAULT_LIMIT } from './request.consts';
import {
  CACHEABLE_REQUEST_STATUS_COMBINATIONS,
  IRequestBlock,
  IRequestOverview,
  REQUEST_STATUS
} from '../../../lib/types';
import { UploadData, UploadFileType, UploadItemData } from '../../_editor/upload/upload.types';
import { CONTAINER_BLOCK_TYPES, NON_FILE_CONTAINER_BLOCK_TYPES } from '../../_editor/_core/editor.const';
import { TITLE_BLOCK_TYPES } from '../../_editor/_core/local-title-block/local-title-block.const';
import { determineLocalTitle } from '../../_editor/_core/local-title-block/local-title-block.utils';
import _ from 'lodash';
import { mergeCacheLists, sanitizeFilenameComponent } from '../../../lib/utils';

export const fillInRequestsCache = ({
  cache,
  includeArchived = false,
  limit = DEFAULT_LIMIT,
  page = 1,
  requestGroup,
  requests,
  skipSubsequentCall,
  status = []
}: IApolloCacheParams &
  IListRequestAsStaffVariables & {
    status?: REQUEST_STATUS[];
    requests: IRequestOverview[];
    skipSubsequentCall?: boolean;
  }) => {
  const statusesToFillIn = status.length
    ? CACHEABLE_REQUEST_STATUS_COMBINATIONS.filter(
      (combo) => !_.isEqual(combo, status) && combo.some((s) => status.includes(s))
    )
    : CACHEABLE_REQUEST_STATUS_COMBINATIONS;

  statusesToFillIn.forEach((combo) => {
    const requestsForCombo = requests.filter((r) => combo.includes(r.status ?? REQUEST_STATUS.DRAFT));
    const currRequestsForCombo = readRequestsCache({ cache, limit, page, requestGroup, status: combo })?.requests ?? [];
    const merged = [
      ...currRequestsForCombo.map((r) => requestsForCombo.find((rfc) => rfc._id === r._id) ?? r),
      ...requestsForCombo.filter((r) => !currRequestsForCombo.find((c) => c._id === r._id))
    ];

    writeRequestsCache({
      cache,
      includeArchived,
      listRequestsAsStaff: { includeArchived, page, status: combo, requests: merged, total: merged.length },
      page,
      limit,
      requestGroup,
      status: combo
    });
  });

  if (!skipSubsequentCall) {
    // Fill in alternate requests list with applicable data from current requests list
    const alternateIncludeArchived = !includeArchived;
    fillInRequestsCache({
      cache,
      includeArchived: alternateIncludeArchived,
      limit,
      page,
      requestGroup,
      requests: alternateIncludeArchived ? requests : requests.filter((r) => !r.deletedAt),
      status,
      skipSubsequentCall: true
    });
  }
};

export const readRequestsCache = ({
  cache,
  includeArchived = false,
  limit = DEFAULT_LIMIT,
  page = 1,
  requestGroup,
  status
}: IApolloCacheParams & IListRequestAsStaffVariables) =>
  cache.readQuery<IListRequestAsStaffResults>({
    query: LIST_REQUESTS_AS_STAFF,
    variables: { includeArchived, limit, page, requestGroup, status }
  })?.listRequestsAsStaff;

export const writeRequestsCache = ({
  cache,
  includeArchived = false,
  listRequestsAsStaff,
  limit = DEFAULT_LIMIT,
  page = 1,
  requestGroup,
  status
}: IApolloCacheParams & IListRequestAsStaffResults & IListRequestAsStaffVariables) =>
  cache.writeQuery({
    query: LIST_REQUESTS_AS_STAFF,
    data: { listRequestsAsStaff: { __typename: 'PaginatedRequests', ...listRequestsAsStaff } },
    variables: { includeArchived, limit, page, requestGroup, status },
    overwrite: true
  });

const readAndUpdateRequestsCache = <T extends IRequestOverview>({
  cache,
  includeArchived = false,
  limit = DEFAULT_LIMIT,
  page = 1,
  remove,
  requestGroup,
  status,
  updated
}: IApolloCacheParams &
  Pagination & {
    includeArchived?: boolean;
    requestGroup?: string;
    remove?: boolean;
    status: REQUEST_STATUS[];
    updated: T[];
  }) => {
  const listRequestsAsStaff = readRequestsCache({
    cache,
    includeArchived,
    limit,
    page,
    requestGroup,
    status
  });

  if (listRequestsAsStaff) {
    // Filter final requests according to includeArchived and status
    const shouldInclude =
      !remove &&
      updated.every((u) => {
        console.log('Should include', {
          shouldInclude:
            status.includes(u.status ?? REQUEST_STATUS.DRAFT) &&
            (includeArchived || !u.deletedAt) &&
            (!requestGroup || u.requestGroup?._id === requestGroup),
          u,
          status,
          includeArchived
        });
        return (
          status.includes(u.status ?? REQUEST_STATUS.DRAFT) &&
          (includeArchived || !u.deletedAt) &&
          (!requestGroup || u.requestGroup?._id === requestGroup)
        );
      });

    const newRequests = shouldInclude
      ? mergeCacheLists(listRequestsAsStaff.requests, updated)
      : listRequestsAsStaff.requests.filter((r) => !updated.some((u) => u._id === r._id));

    cache.writeQuery({
      query: LIST_REQUESTS_AS_STAFF,
      data: { listRequestsAsStaff: { ...listRequestsAsStaff, __typename: 'PaginatedRequests', requests: newRequests } },
      variables: { includeArchived, limit, page, requestGroup, status },
      overwrite: true
    });
  }
};

// Apply updated requests into all cache lists
export const updateRequestsCacheAfterModification = <T extends IRequestOverview>({
  requestGroup,
  ...params
}: IApolloCacheParams & Pagination & { requestGroup?: string; remove?: boolean; updated: T[] }) =>
  CACHEABLE_REQUEST_STATUS_COMBINATIONS.forEach((status) => {
    readAndUpdateRequestsCache({ ...params, status, includeArchived: false });
    readAndUpdateRequestsCache({ ...params, status, includeArchived: true });

    if (requestGroup) {
      readAndUpdateRequestsCache({ ...params, requestGroup, status, includeArchived: false });
      readAndUpdateRequestsCache({ ...params, requestGroup, status, includeArchived: true });
    }
  });

export const findFilesToDownload = ({
  blocks,
  fileType,
  forceLabelsOnDocProvide
}: {
  blocks?: IRequestBlock[];
  fileType?: UploadFileType;
  forceLabelsOnDocProvide?: boolean;
}) => {
  const foundFiles: (UploadItemData & { forceLabel?: boolean; localTitle?: string; tag?: string })[] = [];

  const updateFoundFiles = (
    data: object,
    currIndex: number,
    parentIndices: number[],
    nestedDepthPath: number[],
    currentNestedDepth: number,
    type?: string | null,
    parentKey?: string,
    repeatSectionEntryDepth?: number
  ) => {
    // Pre-filter 'blocks' arrays to only include TITLE block types
    // This ensures iteration index (i) represents position among TITLE blocks only
    let entriesToProcess = Object.entries(data);
    if (parentKey === 'blocks' && Array.isArray(data)) {
      entriesToProcess = entriesToProcess.filter(
        ([_, value]) =>
          !value ||
          !(typeof value === 'object' && value !== null && 'type' in value) ||
          TITLE_BLOCK_TYPES.includes((value as any).type)
      );
    }

    entriesToProcess.forEach(([key, value], i) => {
      if (!value) return;

      // Determine the actual index to use for local title calculation
      // For 'blocks' arrays: use blockIndex from block data if available, otherwise use iteration index (i)
      // For other structures: preserve currIndex (parent block's position) to maintain hierarchy
      const hasBlockIndex = typeof value === 'object' && value !== null && 'data' in value &&
        typeof value.data === 'object' && value.data !== null && 'blockIndex' in value.data;
      const blockIndexValue = hasBlockIndex ? (value.data as any).blockIndex : undefined;
      const actualIndex = parentKey === 'blocks' ? (blockIndexValue ?? i) : currIndex;

      // Check if current value is a container block (upload, docProvide, uploadList)
      // Container blocks should only add their index when entering their 'data' property,
      // not when processed as array elements (to avoid duplicates)
      const isContainerBlock = typeof value === 'object' && value !== null && 'type' in value && CONTAINER_BLOCK_TYPES.includes((value as any).type);

      // Check if current value is a repeatSection block
      const isRepeatSection = typeof value === 'object' && value !== null && 'type' in value && (value as any).type === 'repeatSection';

      // TODO: Extract this out to reusable util function
      // Determine if local title is applied to current data object.
      // Yes if object would represent a visually nested element in the rendered form.
      // Add index for: (numeric keys that aren't containers) OR ('data' key of containers)
      // IMPORTANT: Skip branch-multi sectionsData elements as they represent choice options, not visual nesting
      // This applies even inside repeatSection - branch-multi choices are never visual nesting
      const isBranchMultiSectionsData = parentKey === 'sectionsData' && type === 'branch-multi';
      const includeParentLocalTitle =
        ((!isNaN(parseInt(key)) && !isContainerBlock) ||
          (type && CONTAINER_BLOCK_TYPES.includes(type) && key === 'data')) &&
        key !== 'blocks' &&
        !isBranchMultiSectionsData &&
        type &&
        ([...NON_FILE_CONTAINER_BLOCK_TYPES, 'blocks'].includes(type) ||
          (CONTAINER_BLOCK_TYPES.includes(type) && key === 'data'));

      // When adding to parent indices:
      // - For array elements (numeric key): use actualIndex (element's position in array)
      // - For object properties like 'data': use currIndex (parent object's index)
      // Also track the nestedDepth at each level for correct alternating pattern
      const indexToAdd = !isNaN(parseInt(key)) ? actualIndex : currIndex;
      const nextNestedDepth = includeParentLocalTitle ? currentNestedDepth + 1 : currentNestedDepth;
      const updatedParents = includeParentLocalTitle ? [...parentIndices, indexToAdd] : parentIndices;
      const updatedNestedDepthPath = includeParentLocalTitle ? [...nestedDepthPath, nextNestedDepth] : nestedDepthPath;

      if (key === 'uploads') {
        const uploadData = data as UploadData;
        const foundUploads = uploadData.uploads;
        const uploadTag = uploadData.tag;
        if (!fileType || type === fileType || (fileType === 'upload' && type === 'uploadList')) {
          foundFiles.push(
            ...(forceLabelsOnDocProvide && type === 'docProvide'
              ? foundUploads.map((u) => ({ ...u, forceLabel: true }))
              : foundUploads
            ).map((u, j) => {
              // File has one more depth level beyond what's in nestedDepthPath
              const fileNestedDepth = nextNestedDepth + 1;
              const parentIndicesForFile = [...updatedParents, j];
              const nestedDepthPathForFile = [...updatedNestedDepthPath, fileNestedDepth];

              const localTitle = parentIndicesForFile.reduce((acc, titleIndex, depth) => {
                const nestedDepth = nestedDepthPathForFile[depth];
                let lt: string;

                // Special handling for first block level inside repeatSection
                // The first block level is at repeatSectionEntryDepth + 2
                // (+1 for sectionsData[instance], +1 for the block itself)
                const isFirstBlockLevelInRepeatSection =
                  repeatSectionEntryDepth !== undefined &&
                  nestedDepth === repeatSectionEntryDepth + 2;

                const isInsideRepeatSection =
                  repeatSectionEntryDepth !== undefined &&
                  nestedDepth > repeatSectionEntryDepth + 2;

                if (isFirstBlockLevelInRepeatSection) {
                  // First block level inside repeatSection uses simple numbering
                  lt = String(titleIndex + 1);
                } else if (isInsideRepeatSection) {
                  // Deeper levels inside repeatSection: alternate based on previous character type
                  const prevChar = acc[acc.length - 1];
                  const prevWasNumber = /\d/.test(prevChar);

                  if (prevWasNumber) {
                    // Previous was number, use letter
                    const letterize = (num: number) => String.fromCharCode(96 + num);
                    lt = letterize(titleIndex + 1);
                  } else {
                    // Previous was letter, use number
                    lt = String(titleIndex + 1);
                  }
                } else {
                  // Use alternating pattern based on nestedDepth
                  lt = (depth === 1 ? '.' : '') + determineLocalTitle(nestedDepth, titleIndex);
                }

                return acc + lt;
              }, '');

              return {
                ...u,
                tag: uploadTag,
                localTitle
              };
            })
          );
        }
      } else if (typeof value === 'object' && value !== null) {
        // Track when we enter a repeatSection to enable special handling
        const nextRepeatSectionEntryDepth = isRepeatSection ? nextNestedDepth : repeatSectionEntryDepth;

        updateFoundFiles(
          value,
          actualIndex,
          updatedParents,
          updatedNestedDepthPath,
          nextNestedDepth,
          (typeof value === 'object' && value !== null && 'type' in value) ? value['type'] : type,
          key,
          nextRepeatSectionEntryDepth
        );
      }
    });
  };

  // Process all top-level blocks (typically sections)
  // Filter to only TITLE blocks at the top level to match UI rendering
  const filteredBlocks = blocks?.filter(block => TITLE_BLOCK_TYPES.includes(block.type)) || [];

  filteredBlocks.forEach(({ data }, i) => {
    // Start with nestedDepth 1 for top-level sections (which produce uppercase letters)
    updateFoundFiles(data, i, [i], [1], 1, undefined, undefined, undefined);
  });

  // Return files with local titles appended to file label
  const results = foundFiles
    .filter((f) => f.fileId)
    .map((f) => {
      const fileExt = f.fileName.split('.').pop();

      // Create final file label and ensure file extension is added to end of new label
      // Include tag after local title if present (with defensive sanitization)
      let label = _.unescape(f.localTitle);
      if (f.tag && f.tag.trim()) {
        label += ' - ' + sanitizeFilenameComponent(_.unescape(f.tag.trim()));
      }
      label += ' - ' + _.unescape(f.label ?? '');

      if (fileExt && f.fileName) {
        // Only add file extension if it isn't already present at the end of the file, prevents double extensions when original fileName is used instead of file label
        const fileExtSuffix = '.' + fileExt;
        if (!label.endsWith(fileExtSuffix)) label += fileExtSuffix;
      }

      return { ...f, label };
    });

  return results;
};
